// Customer Follow-ups: "who do I contact next, why, and what do I do?"
//
// Built for a phone first. One big button gives you the next customer; the
// card says why they need a message and suggests one; you open WhatsApp, say
// whether you actually sent it, record what happened, and move on.
//
// The server decides everything that matters - who gets which customer,
// whether a message may go out right now, what counts as done. This screen
// only shows its answers; a hidden button here is not what stops anything.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../api';
import FollowUpsAdmin from './FollowUpsAdmin';

const F = `${API}/api/followups`;
export const GREEN = '#1f8f4e';
const WINE = '#6E1F2A';
const MUTED = '#7d6a63';

export const btn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '10px 14px', borderRadius: 10, fontSize: 14, fontWeight: 600,
  textDecoration: 'none', cursor: 'pointer', border: 'none'
};
export const quiet = { ...btn, background: '#fff', color: '#4a3840', border: '1px solid #e6dccc' };

const WF = {
  failed_delivery: { label: 'Delivery problem', bg: '#fbe9e7', fg: '#8c1d18' },
  abandoned_checkout: { label: 'Unfinished checkout', bg: '#f5f1d6', fg: '#7a6a12' },
  review: { label: 'Review request', bg: '#f3e7e9', fg: WINE }
};

export const ist = (d, withDay = true) => d ? new Date(d).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', ...(withDay ? { day: 'numeric', month: 'short' } : {}), hour: 'numeric', minute: '2-digit'
}) : '';
export const rupees = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
export const errMsg = (e) => e.response?.data?.message || e.message;

// datetime-local works in the browser's clock; staff are in India, but the
// value is pinned to +05:30 so a laptop set to another zone cannot shift it.
const toIstInput = (d) => {
  const t = new Date(new Date(d).getTime() + 330 * 60000);
  return t.toISOString().slice(0, 16);
};
const fromIstInput = (v) => v ? new Date(v + ':00+05:30').toISOString() : null;

export function WfBadge({ wf, urgent }) {
  const w = WF[wf] || { label: wf, bg: '#eee', fg: '#333' };
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <span className="badge" style={{ background: w.bg, color: w.fg }}>{w.label}</span>
      {urgent && <span className="badge" style={{ background: '#b3261e', color: '#fff' }}>Urgent</span>}
    </span>
  );
}

function Banner({ kind = 'warn', children }) {
  const s = kind === 'block'
    ? { background: '#fbe9e7', color: '#8c1d18', border: '1px solid #f0c4bf' }
    : kind === 'ok' ? { background: '#e8f3ec', color: '#2e7d50', border: '1px solid #c5e3cf' }
    : { background: '#fbeee5', color: '#8a5a2b', border: '1px solid #f0d6c0' };
  return <div style={{ ...s, borderRadius: 10, padding: '9px 12px', fontSize: 14, marginBottom: 8 }}>{children}</div>;
}

// ---------------------------------------------------------------- home ---

function Tile({ label, n, active, onClick, tone }) {
  return (
    <button onClick={onClick} className="stat-card" style={{
      textAlign: 'left', cursor: 'pointer', borderLeftColor: tone || 'var(--chartreuse)',
      outline: active ? `2px solid ${WINE}` : 'none', background: active ? '#fff' : undefined
    }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{n}</div>
    </button>
  );
}

function Row({ r, onOpen }) {
  return (
    <div className="card" onClick={() => onOpen(r.contactId)} style={{ padding: 12, marginBottom: 8, cursor: 'pointer' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700 }}>{r.name}</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {(r.workflows || [r.workflow]).filter(Boolean).map(w => <WfBadge key={w} wf={w} />)}
          {r.urgent && <span className="badge" style={{ background: '#b3261e', color: '#fff' }}>Urgent</span>}
        </div>
      </div>
      <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>
        {r.awaitingOutcome ? <b style={{ color: '#8c1d18' }}>Record what happened with your last message</b>
          : r.reason ? r.reason : r.nextAction}
        {r.nextDue && !r.reason && <> · {r.overdue ? <b style={{ color: '#8c1d18' }}>overdue since {ist(r.nextDue)}</b> : `due ${ist(r.nextDue)}`}</>}
        {r.owner && r.showOwner && <> · with {r.owner.split('@')[0]}</>}
        {r.escalatedTo && <> · escalated to {r.escalatedTo === 'admin' ? 'admin' : r.escalatedTo.split('@')[0]}</>}
      </div>
    </div>
  );
}

function Home({ me, onOpen }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tile, setTile] = useState('due');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${F}/home`)).data); setError(null); }
    catch (e) { setError(errMsg(e)); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const startNext = async () => {
    setBusy(true); setNote(null);
    try {
      const { data: r } = await axios.post(`${F}/claim`);
      if (r.contactId) onOpen(r.contactId, r.needsOutcome ? 'Record what happened with your last message before taking a new customer.' : r.resumed ? 'You already had this customer reserved — finish or put them back first.' : null);
      else { setNote(r.message); load(); }
    } catch (e) { setNote(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (error) return <div className="card" style={{ color: '#b3261e' }}>{error}</div>;
  if (!data) return <div style={{ color: MUTED }}>Loading…</div>;

  const admin = data.admin;
  const helpRows = [
    ...data.help,
    ...(admin ? [
      ...admin.escalatedToAdmin.map(r => ({ ...r, showOwner: true })),
      ...admin.heldByUnavailableStaff.map(r => ({ ...r, showOwner: true, reason: `Held by ${r.owner?.split('@')[0]}, who is unavailable — transfer or release` })),
      ...admin.overdue.map(r => ({ ...r, showOwner: true })),
      ...admin.inactive.map(r => ({ ...r, showOwner: true, reason: `No action by ${r.owner?.split('@')[0]} since ${ist(r.lastActivityAt)}` }))
    ] : [])
  ].filter((r, i, a) => a.findIndex(x => String(x.contactId) === String(r.contactId)) === i);

  const lists = {
    due: data.due, waiting: data.waiting, help: helpRows, holding: data.holding,
    done: data.completedToday.map(c => ({ contactId: c.contactId, name: c.name || 'Customer', workflow: c.workflow, reason: `${c.reason} · ${ist(c.at, false)}` }))
  };
  const canWork = me.perms.includes('work');
  const blocked = !data.enabled || !data.withinContactHours || !canWork || !me.available;

  return (
    <div>
      <div className="card" style={{ textAlign: 'center' }}>
        {!data.enabled && <Banner>Customer Follow-ups is switched off while it is being set up. {me.perms.includes('manage') ? 'Open Admin → Setup to preview and switch it on.' : ''}</Banner>}
        {data.enabled && !data.withinContactHours && <Banner>Outside contact hours ({data.contactHours.start}–{data.contactHours.end}). New customers open up then.</Banner>}
        {data.enabled && !me.available && <Banner>You are marked unavailable, so the queue will not give you new customers. Your own customers are below.</Banner>}
        {canWork && (
          <button onClick={startNext} disabled={busy || blocked}
            style={{ ...btn, background: blocked ? '#bdb2ab' : GREEN, color: '#fff', width: '100%', padding: '18px', fontSize: 19, borderRadius: 14 }}>
            {busy ? 'Finding the next customer…' : 'Start next customer →'}
          </button>
        )}
        {canWork && data.enabled && <div style={{ fontSize: 13, color: MUTED, marginTop: 8 }}>{data.queueWaiting} waiting in the queue for you</div>}
        {note && <div style={{ marginTop: 10, fontSize: 15 }}>{note}</div>}
      </div>

      <div className="stat-grid">
        <Tile label="My follow-ups due" n={lists.due.length} active={tile === 'due'} onClick={() => setTile('due')} tone={lists.due.some(r => r.overdue) ? '#b3261e' : undefined} />
        <Tile label="Waiting for customer" n={lists.waiting.length} active={tile === 'waiting'} onClick={() => setTile('waiting')} />
        <Tile label="Needs owner/admin help" n={lists.help.length} active={tile === 'help'} onClick={() => setTile('help')} tone={lists.help.length ? '#c05621' : undefined} />
        <Tile label="Completed today" n={lists.done.length} active={tile === 'done'} onClick={() => setTile('done')} tone="#2e7d50" />
        <Tile label="All my customers" n={lists.holding.length} active={tile === 'holding'} onClick={() => setTile('holding')} />
      </div>

      {lists[tile].length === 0
        ? <div className="card" style={{ textAlign: 'center', color: MUTED }}>Nothing here.</div>
        : lists[tile].map(r => <Row key={String(r.contactId) + tile} r={r} onOpen={onOpen} />)}
    </div>
  );
}

// ------------------------------------------------------------- customer ---

const NEXT_LABEL = { followup: 'Follow up at', waiting: 'Wait for customer until', escalate: 'Escalate', close: 'Close' };

function OutcomeForm({ c, people, onDone, compact }) {
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const def = c.outcomes.find(o => o.key === outcome);
  const [type, setType] = useState('');
  const [at, setAt] = useState('');
  const [to, setTo] = useState('admin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!def) return;
    setType(def.next[0]);
    setAt(toIstInput(Date.now() + (def.defaultHours || 24) * 3600000));
  }, [outcome]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const next = { type };
      if (type === 'followup' || type === 'waiting') next.at = fromIstInput(at);
      if (type === 'escalate') { next.to = to; next.reason = note; }
      await axios.post(`${F}/cases/${c.id}/outcome`, { outcome, note, next, date: def?.needDate ? fromIstInput(at) : undefined });
      onDone();
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ border: '1px solid #e6dccc', borderRadius: 10, padding: 12, background: '#fdfbf7', marginTop: 8 }}>
      {!compact && <div style={{ fontSize: 12, fontWeight: 700, color: MUTED, marginBottom: 8 }}>WHAT HAPPENED? · {WF[c.workflow]?.label}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {c.outcomes.map(o => (
          <button key={o.key} onClick={() => setOutcome(o.key)} style={{
            ...btn, padding: '8px 12px', fontSize: 13, fontWeight: 500,
            background: outcome === o.key ? WINE : '#fff', color: outcome === o.key ? '#F8F3EB' : '#4a3840',
            border: '1px solid ' + (outcome === o.key ? WINE : '#e6dccc')
          }}>{o.label}</button>
        ))}
      </div>
      {def && (
        <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
          <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
            placeholder={def.needNote || type === 'escalate' ? 'Note (required)' : 'Note (optional) — what did the customer say?'}
            style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 8, border: '1px solid #e6dccc', fontFamily: 'inherit', fontSize: 14 }} />
          <div style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>NEXT STEP</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {def.next.map(t => (
              <label key={t} style={{ ...quiet, padding: '7px 11px', fontSize: 13, fontWeight: 500, background: type === t ? '#eef8f1' : '#fff', borderColor: type === t ? GREEN : '#e6dccc' }}>
                <input type="radio" checked={type === t} onChange={() => setType(t)} style={{ width: 'auto', margin: 0 }} /> {NEXT_LABEL[t]}
              </label>
            ))}
          </div>
          {(type === 'followup' || type === 'waiting' || def.needDate) && type !== 'escalate' && type !== 'close' && (
            <label style={{ fontSize: 13 }}>{def.needDate ? 'Available from' : 'When'} (India time)
              <input type="datetime-local" value={at} onChange={e => setAt(e.target.value)} style={{ display: 'block', marginTop: 4 }} />
            </label>
          )}
          {type === 'escalate' && (
            <label style={{ fontSize: 13 }}>Escalate to
              <select value={to} onChange={e => setTo(e.target.value)} style={{ display: 'block', marginTop: 4 }}>
                <option value="admin">Admin (whoever handles escalations)</option>
                {people.map(p => <option key={p.email} value={p.email}>{p.name}</option>)}
              </select>
            </label>
          )}
          {error && <div style={{ color: '#b3261e', fontSize: 13 }}>{error}</div>}
          <button onClick={save} disabled={busy} style={{ ...btn, background: WINE, color: '#F8F3EB' }}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      )}
    </div>
  );
}

function DeliveryFacts({ d }) {
  const step = (done, label, when, warn) => (
    <div style={{ display: 'flex', gap: 8, fontSize: 13, color: done ? '#2e7d50' : warn ? '#8c1d18' : MUTED }}>
      <span>{done ? '✓' : '○'}</span><span>{label}{when ? ` · ${ist(when)}` : ''}</span>
    </div>
  );
  return (
    <div style={{ display: 'grid', gap: 2, margin: '8px 0' }}>
      {step(!!d.customerConfirmedAt, 'Customer confirmed', d.customerConfirmedAt)}
      {d.unavailableUntil && step(false, `Customer unavailable until ${ist(d.unavailableUntil)}`)}
      {step(!!d.reattemptRequestedAt, d.reattemptRequestedAt ? `Reattempt submitted to courier (${d.reattemptVia === 'delhivery_api' ? 'via Delhivery API' : `in Delhivery panel by ${d.reattemptRequestedBy?.split('@')[0]}`})` : d.reattemptTaskOpen ? 'Reattempt NOT yet submitted to the courier — waiting for the person who does it' : 'Reattempt not requested', d.reattemptRequestedAt, d.reattemptTaskOpen)}
      {step(!!d.courierAckAt, 'Courier acknowledged the reattempt (their scan)', d.courierAckAt)}
      {step(d.terminal === 'delivered', d.terminal ? `Parcel ${d.terminal}` : 'Not delivered yet')}
    </div>
  );
}

function CaseDetail({ c }) {
  const line = (k, v) => v ? <div style={{ fontSize: 13 }}><span style={{ color: MUTED }}>{k}: </span>{v}</div> : null;
  const items = (xs) => (xs || []).map((i, k) => <div key={k} style={{ fontSize: 14 }}>• {i.name || i.title} <span style={{ color: MUTED }}>— {[i.colour, i.size].filter(Boolean).join(', ')}{(i.qty || i.quantity) > 1 ? ` ×${i.qty || i.quantity}` : ''}</span></div>);
  if (c.workflow === 'failed_delivery') {
    const s = c.shipment || {}, o = c.order || {};
    return (
      <div>
        {line('Courier', `${s.courier || 'Delhivery'} · AWB ${s.awb}`)}
        {line('Latest status', [s.status, s.statusText, s.location].filter(Boolean).join(' · '))}
        {line('Attempts', (s.history || []).map(h => `${ist(h.at)}: ${h.reason}`).join(' | '))}
        {s.actBy && line('Act by', ist(s.actBy))}
        {line('Order', o.number)}
        {items(o.items)}
        {line('Payment', o.payment === 'COD' ? `COD — collect ${rupees(o.codAmount)}${o.paidInFlight ? ' (customer has since paid online — check before they pay twice)' : ''}` : `Prepaid ${rupees(o.total)}`)}
        {line('Address', o.address)}
        {o.phoneOnOrder && line('Phone on order', o.phoneOnOrder)}
        {o.codReminderSentAt && line('Automated COD reminder', `sent ${ist(o.codReminderSentAt)} (Interakt)`)}
        <DeliveryFacts d={c.delivery} />
      </div>
    );
  }
  if (c.workflow === 'abandoned_checkout') {
    return (
      <div>
        {(c.checkouts || []).map((k, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 13, color: MUTED }}>{ist(k.at)} · {rupees(k.total)}{k.stage ? ` · stopped at "${k.stage}"` : ''}</div>
            {items(k.items)}
            {k.url && <div style={{ fontSize: 12 }}><a href={k.url} target="_blank" rel="noopener noreferrer">Recovery link</a> (opens their cart)</div>}
          </div>
        ))}
        {c.purchase && <Banner kind="ok">Ordered {c.purchase.orderNumber} on {ist(c.purchase.at)} — {c.purchase.attributionNote}</Banner>}
      </div>
    );
  }
  const r = c.review || {};
  return (
    <div>
      {(c.orders || []).map((o, i) => <div key={i} style={{ marginBottom: 4 }}><div style={{ fontSize: 13, color: MUTED }}>{o.number} · delivered {ist(o.deliveredAt)}</div>{items(o.items)}</div>)}
      <div style={{ fontSize: 13, marginTop: 6, color: MUTED }}>
        {r.linkSharedAt ? `✓ Link shared ${ist(r.linkSharedAt)}` : '○ Link not shared yet'} · {r.customerRepliedAt ? `✓ Replied ${ist(r.customerRepliedAt)}` : '○ No reply recorded'} · {r.receivedAt ? `✓ Review received (${r.verified ? 'verified' : 'confirmed by staff, not verified'})` : '○ No review seen yet'}
      </div>
      {r.issueAt && <Banner kind="block">Customer reported a problem {ist(r.issueAt)} — sort that out; no review reminders until then.</Banner>}
    </div>
  );
}

function CaseActions({ c, me, people, isMine, reload }) {
  const [mode, setMode] = useState(null);
  const [val, setVal] = useState('');
  const [reason, setReason] = useState('no_response');
  const [error, setError] = useState(null);
  const post = async (url, body) => {
    setError(null);
    try { await axios.post(`${F}${url}`, body); setMode(null); setVal(''); reload(); }
    catch (e) { setError(errMsg(e)); }
  };
  const canCourier = me.perms.includes('courier');
  const admin = me.perms.includes('assign');
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {(isMine || admin) && <button style={{ ...quiet, fontSize: 13 }} onClick={() => setMode(mode === 'outcome' ? null : 'outcome')}>Record outcome</button>}
        {(isMine || admin) && <button style={{ ...quiet, fontSize: 13 }} onClick={() => setMode(mode === 'note' ? null : 'note')}>Add note</button>}
        {(isMine || admin) && c.workflow !== 'failed_delivery' && <button style={{ ...quiet, fontSize: 13 }} onClick={() => setMode(mode === 'close' ? null : 'close')}>Close case</button>}
        {c.workflow === 'review' && (isMine || admin) && <button style={{ ...quiet, fontSize: 13 }} onClick={() => setMode(mode === 'review' ? null : 'review')}>I saw their review</button>}
        {c.workflow === 'failed_delivery' && canCourier && !c.delivery.reattemptRequestedAt && <button style={{ ...quiet, fontSize: 13 }} onClick={() => setMode(mode === 'reattempt' ? null : 'reattempt')}>Reattempt submitted</button>}
        {c.status === 'escalated' && (admin || c.escalatedTo === me.email) && <button style={{ ...quiet, fontSize: 13 }} onClick={() => setMode(mode === 'resolve' ? null : 'resolve')}>Handle escalation</button>}
      </div>
      {mode === 'outcome' && <OutcomeForm c={c} people={people} onDone={() => { setMode(null); reload(); }} />}
      {['note', 'close', 'review', 'reattempt', 'resolve'].includes(mode) && (
        <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
          {mode === 'close' && (
            <select value={reason} onChange={e => setReason(e.target.value)}>
              <option value="no_response">No response after follow-ups</option>
              <option value="not_interested">Not interested</option>
              <option value="helped">Helped — no order yet</option>
              <option value="declined">Doesn't want to review</option>
              <option value="duplicate">Duplicate</option>
              <option value="other">Other</option>
            </select>
          )}
          {mode === 'resolve' && <div style={{ fontSize: 13, color: MUTED }}>Escalated: {c.escalationReason}</div>}
          <textarea rows={2} value={val} onChange={e => setVal(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 8, border: '1px solid #e6dccc', fontFamily: 'inherit' }}
            placeholder={{ note: 'Note', close: 'Why is it being closed? (required)', review: 'Where did you see it? e.g. "Google, 5 Oct, as Priya S" (required)', reattempt: 'Optional note', resolve: 'What was decided? The owner will see this. (required)' }[mode]} />
          {mode === 'review' && <div style={{ fontSize: 12, color: MUTED }}>Saved as "confirmed by staff, not verified" — reports keep it separate from verified reviews.</div>}
          {mode === 'reattempt' && <div style={{ fontSize: 12, color: MUTED }}>Only press this after you have actually submitted the reattempt in the Delhivery panel.</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {mode === 'note' && <button style={{ ...btn, background: WINE, color: '#fff' }} onClick={() => post(`/cases/${c.id}/note`, { note: val })}>Save note</button>}
            {mode === 'close' && <button style={{ ...btn, background: WINE, color: '#fff' }} onClick={() => post(`/cases/${c.id}/close`, { reason, note: val })}>Close case</button>}
            {mode === 'review' && <button style={{ ...btn, background: WINE, color: '#fff' }} onClick={() => post(`/cases/${c.id}/review-confirm`, { note: val })}>Confirm review (manual)</button>}
            {mode === 'reattempt' && <button style={{ ...btn, background: WINE, color: '#fff' }} onClick={() => post(`/cases/${c.id}/reattempt`, { via: 'manual_panel', confirm: true, note: val })}>I submitted it in Delhivery</button>}
            {mode === 'resolve' && <>
              <button style={{ ...btn, background: WINE, color: '#fff' }} onClick={() => post(`/cases/${c.id}/resolve-escalation`, { action: 'return', note: val })}>Send back to owner</button>
              <button style={quiet} onClick={() => post(`/cases/${c.id}/resolve-escalation`, { action: 'close', reason: 'admin_closed', note: val })}>Close it</button>
            </>}
          </div>
        </div>
      )}
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

function Composer({ card, reload, onSentNeedsOutcome }) {
  const usable = card.message.caseIds.map(String);
  const [picked, setPicked] = useState(usable);
  const [text, setText] = useState(card.message.suggested);
  const [offer, setOffer] = useState('');
  const [pending, setPending] = useState(null);   // { openEventId, waUrl }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPicked(card.message.caseIds.map(String)); setText(card.message.suggested); }, [card.message.suggested]); // eslint-disable-line react-hooks/exhaustive-deps

  const withOffer = useMemo(() => {
    const o = card.message.offers.find(x => x.code === offer);
    return o ? `${text}\n\n${o.description} — use code ${o.code}.` : text;
  }, [text, offer, card.message.offers]);

  const open = async () => {
    setBusy(true); setError(null);
    // Open the tab synchronously (popup blockers), then point it at WhatsApp
    // only once the server has said yes.
    const win = window.open('', '_blank');
    try {
      const { data } = await axios.post(`${F}/contacts/${card.contact.id}/open-whatsapp`, { caseIds: picked, text: withOffer, offerCode: offer || undefined });
      if (win) win.location.href = data.waUrl; else window.location.href = data.waUrl;
      setPending(data);
    } catch (e) {
      if (win) win.close();
      setError(errMsg(e));
      reload();
    } finally { setBusy(false); }
  };

  const confirm = async (sent) => {
    setBusy(true);
    try {
      await axios.post(`${F}/contacts/${card.contact.id}/message-result`, { openEventId: pending.openEventId, sent });
      setPending(null);
      if (sent) onSentNeedsOutcome(pending.includedCaseIds.map(String));
      reload();
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (pending) {
    return (
      <div className="card" style={{ border: `2px solid ${GREEN}`, background: '#eef8f1' }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Did you send the message?</div>
        <div style={{ fontSize: 14, color: MUTED, marginBottom: 12 }}>WhatsApp opened in another tab. Only say yes if you pressed Send there.</div>
        {pending.warnings?.map((w, i) => <Banner key={i}>{w}</Banner>)}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button disabled={busy} onClick={() => confirm(true)} style={{ ...btn, background: GREEN, color: '#fff', flex: '1 1 160px', padding: 14, fontSize: 16 }}>Yes, I sent it</button>
          <button disabled={busy} onClick={() => confirm(false)} style={{ ...quiet, flex: '1 1 160px', padding: 14, fontSize: 16 }}>No, I didn't send it</button>
        </div>
        <div style={{ marginTop: 8 }}><a href={pending.waUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>Open WhatsApp again</a></div>
      </div>
    );
  }

  const pickable = card.cases.filter(c => c.status !== 'escalated');
  return (
    <div className="card">
      <div className="card-title" style={{ marginBottom: 10 }}>Message</div>
      {card.checks.blockers.map((b, i) => <Banner key={i} kind="block">{b}</Banner>)}
      {card.checks.warnings.map((w, i) => <Banner key={i}>{w}</Banner>)}
      {pickable.length > 1 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8, fontSize: 13 }}>
          {pickable.map(c => (
            <label key={c.id} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={picked.includes(String(c.id))}
                onChange={() => setPicked(p => p.includes(String(c.id)) ? p.filter(x => x !== String(c.id)) : [...p, String(c.id)])} />
              {WF[c.workflow].label}
            </label>
          ))}
        </div>
      )}
      <textarea rows={9} value={text} onChange={e => setText(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: 10, borderRadius: 8, border: '1px solid #e6dccc', fontFamily: 'inherit' }} />
      {card.message.offers.length > 0 && (
        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>Approved offer (optional — never invent one)
          <select value={offer} onChange={e => setOffer(e.target.value)} style={{ display: 'block', marginTop: 4 }}>
            <option value="">No offer</option>
            {card.message.offers.map(o => <option key={o.code} value={o.code}>{o.code} — {o.description}</option>)}
          </select>
        </label>
      )}
      {error && <Banner kind="block">{error}</Banner>}
      <button onClick={open} disabled={busy || !card.checks.ok || !picked.length}
        style={{ ...btn, marginTop: 10, width: '100%', padding: 14, fontSize: 16, background: card.checks.ok && picked.length ? GREEN : '#bdb2ab', color: '#fff' }}>
        {busy ? 'Checking…' : 'Open WhatsApp'}
      </button>
      <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>Everything is re-checked first. This app cannot see WhatsApp — you'll be asked whether you sent it.</div>
    </div>
  );
}

function History({ items, canCorrect, reload }) {
  const [open, setOpen] = useState(false);
  const fix = async (e) => {
    const note = window.prompt('What was wrong with this entry?');
    if (!note) return;
    const undoSend = e.type === 'message_sent' && window.confirm('Was the message actually NOT sent? OK = yes, take the attempt back off.');
    try { await axios.post(`${F}/events/${e.id}/correct`, { note, undoSend }); reload(); }
    catch (err) { alert(errMsg(err)); }
  };
  const shown = open ? items : items.slice(0, 6);
  return (
    <div className="card">
      <div className="card-title" style={{ marginBottom: 10 }}>History</div>
      {shown.map(e => (
        <div key={e.id} style={{ fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f0e9dd', opacity: e.corrected ? 0.55 : 1 }}>
          <span style={{ color: MUTED }}>{ist(e.at)} · {e.who}</span> — <b>{e.text}</b>
          {e.corrected && <span style={{ color: '#8c1d18' }}> (corrected)</span>}
          {e.note && <div style={{ whiteSpace: 'pre-wrap' }}>{e.note}</div>}
          {e.type === 'message_sent' && e.data?.text && <details><summary style={{ cursor: 'pointer', color: MUTED }}>message</summary><div style={{ whiteSpace: 'pre-wrap' }}>{e.data.text}</div></details>}
          {canCorrect && !e.corrected && ['message_sent', 'outcome', 'note', 'reattempt_requested'].includes(e.type) && e.who !== 'System' &&
            <button onClick={() => fix(e)} style={{ background: 'none', border: 'none', color: WINE, textDecoration: 'underline', fontSize: 12, padding: 0, cursor: 'pointer' }}>Correct this</button>}
        </div>
      ))}
      {items.length > 6 && <button onClick={() => setOpen(!open)} style={{ ...quiet, marginTop: 8, fontSize: 13 }}>{open ? 'Show less' : `Show all ${items.length}`}</button>}
    </div>
  );
}

function Customer({ id, me, people, onBack, onNext, flash }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState(null);
  const [needOutcome, setNeedOutcome] = useState([]);
  const [transferTo, setTransferTo] = useState('');

  const load = useCallback(async (fresh) => {
    try {
      const { data } = await axios.get(`${F}/contacts/${id}`, { params: fresh ? { fresh: 1 } : {} });
      setCard(data); setError(null);
      // Anything messaged with nothing recorded yet stays on screen until it is.
      setNeedOutcome(data.cases.filter(c => c.awaitingOutcome).map(c => String(c.id)));
    } catch (e) { setError(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(true); }, [load]);

  if (error) return <div><button style={quiet} onClick={onBack}>← Back</button><div className="card" style={{ color: '#b3261e', marginTop: 10 }}>{error}</div></div>;
  if (!card) return <div style={{ color: MUTED }}>Checking the latest for this customer…</div>;

  const k = card.contact;
  const isMine = k.isMine;
  const admin = me.perms.includes('assign');
  const putBack = async () => {
    const reason = window.prompt('Why are you putting this customer back? (e.g. "wrong language", "busy")');
    if (reason === null) return;
    try { await axios.post(`${F}/contacts/${k.id}/release`, { reason: reason || 'put back' }); onBack(); }
    catch (e) { alert(errMsg(e)); }
  };
  const optOut = async () => {
    const all = window.confirm('Does the customer want NO messages at all, even about a parcel?\n\nOK = no messages at all\nCancel = just no offers / review requests');
    const reason = window.prompt('What exactly did the customer say?');
    if (!reason) return;
    try { await axios.post(`${F}/contacts/${k.id}/opt-out`, { scope: all ? 'all' : 'promotional', reason }); load(); }
    catch (e) { alert(errMsg(e)); }
  };
  const transfer = async () => {
    const reason = window.prompt(`Why move this customer to ${transferTo}?`);
    if (!reason) return;
    try { await axios.post(`${F}/contacts/${k.id}/transfer`, { to: transferTo, reason }); load(); }
    catch (e) { alert(errMsg(e)); }
  };
  const done = card.cases.length === 0 || (needOutcome.length === 0 && card.cases.every(c => c.status !== 'open'));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
        <button style={quiet} onClick={onBack}>← My follow-ups</button>
        {isMine && done && me.perms.includes('work') && <button style={{ ...btn, background: GREEN, color: '#fff' }} onClick={onNext}>Next customer →</button>}
      </div>
      {flash && <Banner>{flash}</Banner>}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{k.name}</div>
            {k.otherNames.length > 0 && <div style={{ fontSize: 12, color: MUTED }}>Also ordered as: {k.otherNames.join(', ')}</div>}
            <div style={{ fontSize: 15, marginTop: 2 }}>📱 <a href={`tel:+${k.waNumber}`} style={{ color: '#2e1f23' }}>{k.phone}</a></div>
          </div>
          <div style={{ fontSize: 12, color: MUTED, textAlign: 'right' }}>
            {k.ownerState === 'reserved' && isMine && <div>Reserved for you until {ist(k.reservedUntil, false)}</div>}
            {k.ownerState === 'active' && <div>Conversation with {isMine ? 'you' : k.owner?.split('@')[0]}</div>}
            {!isMine && k.owner && k.ownerState === 'reserved' && <div>Reserved by {k.owner.split('@')[0]}</div>}
            <div>{k.lastMessageAt ? `Last message ${ist(k.lastMessageAt)} by ${k.lastMessageBy?.split('@')[0]}` : 'Never messaged'}</div>
            {k.optOut.promotional && <div style={{ color: '#8c1d18' }}>No offers / review requests</div>}
            {k.optOut.all && <div style={{ color: '#8c1d18' }}>No messages at all</div>}
          </div>
        </div>
        {card.automationNote && <details style={{ marginTop: 8, fontSize: 12, color: MUTED }}><summary style={{ cursor: 'pointer' }}>About automated messages</summary>{card.automationNote}</details>}
      </div>

      {card.cases.length === 0 && <Banner kind="ok">Nothing open for this customer any more.</Banner>}

      {card.cases.map(c => (
        <div key={c.id} className="card" style={{ borderLeft: `4px solid ${c.urgent ? '#b3261e' : WF[c.workflow].fg}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <WfBadge wf={c.workflow} urgent={c.urgent} />
            <span style={{ fontSize: 12, color: MUTED }}>{c.statusLabel} · {c.attempts}/{c.maxAttempts} messages</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{c.why}</div>
          <CaseDetail c={c} />
          <div style={{ fontSize: 13, marginTop: 6 }}>
            <span style={{ color: MUTED }}>Next: </span>{c.nextAction}{c.dueAt && c.status !== 'open' ? ` · ${ist(c.dueAt)}` : ''}
            {c.status === 'escalated' && <div style={{ color: '#8a5a2b' }}>Escalated to {c.escalatedTo === 'admin' ? 'admin' : c.escalatedTo?.split('@')[0]}: {c.escalationReason}</div>}
          </div>
          {needOutcome.includes(String(c.id))
            ? <OutcomeForm c={c} people={people} onDone={() => load()} />
            : <CaseActions c={c} me={me} people={people} isMine={isMine} reload={() => load()} />}
        </div>
      ))}

      {isMine && card.cases.length > 0 && me.perms.includes('work') &&
        <Composer card={card} reload={() => load()} onSentNeedsOutcome={(ids) => setNeedOutcome(ids)} />}

      {(isMine || admin) && card.cases.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {isMine && k.ownerState === 'reserved' && <button style={quiet} onClick={putBack}>Put back in queue</button>}
          <button style={quiet} onClick={optOut}>Customer asked not to be messaged</button>
          {admin && (
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <select value={transferTo} onChange={e => setTransferTo(e.target.value)} style={{ width: 'auto' }}>
                <option value="">Transfer to…</option>
                {people.filter(p => p.canWork && p.email !== k.owner).map(p => <option key={p.email} value={p.email}>{p.name}</option>)}
              </select>
              {transferTo && <button style={quiet} onClick={transfer}>Transfer</button>}
            </span>
          )}
        </div>
      )}

      {card.closedCases.length > 0 && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 8 }}>Earlier follow-ups</div>
          {card.closedCases.map(c => <div key={c.id} style={{ fontSize: 13, padding: '4px 0' }}><WfBadge wf={c.workflow} /> {c.closedReason} · {ist(c.closedAt)}</div>)}
        </div>
      )}

      <History items={card.history} canCorrect={isMine || admin} reload={() => load()} />
    </div>
  );
}

// ---------------------------------------------------------------- page ---

export default function FollowUps() {
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [people, setPeople] = useState([]);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([axios.get(`${F}/me`), axios.get(`${F}/people`)]);
        setMe(m.data); setPeople(p.data.people);
      } catch (e) { setError(errMsg(e)); }
    })();
  }, []);

  const contactId = params.get('c');
  const tab = params.get('tab') || 'work';
  const go = (next) => setParams(next, { replace: false });
  const openCustomer = (id, msg) => { setFlash(msg || null); go({ c: String(id) }); };

  const nextCustomer = async () => {
    try {
      const { data } = await axios.post(`${F}/claim`);
      if (data.contactId) openCustomer(data.contactId, data.needsOutcome ? 'Record what happened with your last message first.' : null);
      else { setFlash(null); go({}); setTimeout(() => alert(data.message), 50); }
    } catch (e) { alert(errMsg(e)); }
  };

  if (error) return <div className="card" style={{ color: '#b3261e' }}>{error}</div>;
  if (!me) return <div style={{ color: MUTED }}>Loading…</div>;
  const adminTabs = ['manage', 'assign', 'team_view', 'reports', 'export', 'courier'].some(p => me.perms.includes(p));

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      {!contactId && adminTabs && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {['work', 'admin'].map(t => (
            <button key={t} onClick={() => go(t === 'work' ? {} : { tab: t })} style={{
              ...btn, padding: '7px 14px', fontSize: 13,
              background: tab === t ? WINE : '#fff', color: tab === t ? '#F8F3EB' : '#4a3840', border: '1px solid ' + (tab === t ? WINE : '#e6dccc')
            }}>{t === 'work' ? 'My work' : 'Admin'}</button>
          ))}
        </div>
      )}
      {contactId
        ? <Customer key={contactId} id={contactId} me={me} people={people} flash={flash} onBack={() => { setFlash(null); go({}); }} onNext={nextCustomer} />
        : tab === 'admin' && adminTabs
          ? <FollowUpsAdmin me={me} people={people} onOpen={(id) => openCustomer(id)} />
          : <Home me={me} onOpen={openCustomer} />}
    </div>
  );
}
